import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle, BarChart3, TrendingUp, Target, Users, MessageSquare, Eye, Zap, LineChart, Shield, GitBranch, Bell, Sparkles, Clock, Activity, AlertTriangle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import HowForsytRollsIn from '../components/HowForsytRollsIn';

/* ═══════ SHARED STACK / INTEGRATION LOGOS ═══════ */
const STACK_LOGOS = [
  { name: 'Salesforce', src: null, color: '#00A1E0' },
  { name: 'Zoho', src: null, color: '#E42527' },
  { name: 'Gong', src: '/assets/dcxyztic.png' },
  { name: 'Gmail', src: '/assets/0hye7w5y.png' },
  { name: 'Slack', src: '/assets/yi9s0mbf.webp' },
  { name: 'Jira', src: '/assets/sbre7usi.png' },
];

const IMG_RISK_RADAR = '/product-risk-radar.webp';
const IMG_OPPS = '/product-opportunities.webp';
const IMG_FOCUS = '/product-mail-intelligence.webp';

const STAGE_MAP = {
  'account-intelligence': 'Crawl', 'crm-auto-sync': 'Crawl',
  'risk-radar': 'Walk', 'opportunities-pipeline': 'Walk', 'pipeline-health-scoring': 'Walk', 'deal-risk-signals': 'Walk',
  'todays-focus': 'Run', 'action-prescriptions': 'Run', 'smart-notifications': 'Run',
  'teams-overview': 'Run', 'ai-deal-chat': 'Run', 'role-based-views': 'Run',
};
const STAGE_BLURB = {
  Crawl: 'Capture everything, without typing',
  Walk: 'Learn why you win and why you lose',
  Run: 'Execute the next move for every deal',
};

const productsData = {
  'risk-radar': {
    title: 'Risk Radar',
    icon: TrendingUp,
    headline: 'Spot stalled deals before they slip through the cracks.',
    subheadline: 'Risk Radar continuously monitors every deal in your pipeline and surfaces health scores, risk signals, and recommended interventions, so your team acts before it\'s too late.',
    previewImage: IMG_RISK_RADAR,
    metrics: [
      { value: '12+', label: 'risk signal types tracked per deal' },
      { value: '4-tier', label: 'deal health scoring (Healthy → Critical)' },
      { value: '<24h', label: 'from risk detection to rep notification' },
      { value: '35%', label: 'fewer silent slip losses reported by pilots' },
    ],
    howItWorks: [
      'Forsyt ingests deal data from your CRM, call intelligence, and email activity in real-time.',
      'Each deal is scored on a 4-tier health scale: Healthy, Needs Attention, At Risk, or Critical.',
      'Scoring factors include: days in stage, last activity date, contact engagement depth, email response rate, and call sentiment.',
      'Deals with no activity for 7+ days are auto-flagged. Stalled stage transitions trigger instant alerts to the deal owner and their manager.',
    ],
    benefits: [
      { title: 'Early Warning System', desc: 'See risk before it becomes a missed number. Deals are scored daily so nothing slips quietly.' },
      { title: 'Pipeline-Level View', desc: 'CROs see aggregate pipeline health across the entire org. Filter by team, rep, stage, or risk tier.' },
      { title: 'Actionable, Not Informational', desc: 'Every flagged deal comes with a recommended next action, not just a red icon.' },
      { title: 'Historical Patterns', desc: 'Forsyt learns from your historical win/loss patterns to predict which current deals are trending toward loss.' },
    ],
    example: {
      title: 'A day in the life of Risk Radar',
      steps: [
        { time: '6:30 AM', text: 'Overnight, Forsyt re-scores every deal using the last 24h of signals.' },
        { time: '9:00 AM', text: 'Your CRO opens the Risk Radar and sees 7 deals that deteriorated overnight.' },
        { time: '9:05 AM', text: 'One click reveals the specific signal: champion at Acme has gone quiet for 11 days.' },
        { time: '9:15 AM', text: 'Rep receives a prescribed action: "Multi-thread to VP Engineering with ROI case study".' },
        { time: '4:00 PM', text: 'Rep completes the action; Risk Radar re-scores the deal back to Needs Attention.' },
      ],
    },
    faq: [
      { q: 'Does Risk Radar require new training data?', a: 'No. It starts working from day one using your existing CRM fields and activity data. Over weeks it adapts to your specific win patterns.' },
      { q: 'Can managers override or dismiss a risk signal?', a: 'Yes. Every signal can be acknowledged, snoozed, or dismissed with a reason, and Forsyt learns from those overrides.' },
      { q: 'What CRMs are supported?', a: 'Salesforce, Zoho CRM, and LeadSquared today. HubSpot is on the 2026 roadmap.' },
    ],
    whoItsFor: ['CRO', 'VP Sales', 'Sales Manager', 'RevOps'],
    sandboxLink: '/dashboard/risk-radar',
    related: ['pipeline-health-scoring', 'deal-risk-signals', 'smart-notifications'],
    quote: 'The dashboard should show pipeline health based on stages and the actions depending on them.',
    quoteAuthor: 'Chaitanya Kakkar',
    quoteRole: 'VP - Business & International Expansion, SingleInterface',
  },
  'opportunities-pipeline': {
    title: 'Opportunities Pipeline',
    icon: BarChart3,
    headline: 'Full deal tracking with risk scoring, stage analysis, and smart filters.',
    subheadline: 'See every deal in your pipeline with AI-powered health scores, stage-by-stage analysis, and the ability to drill into any account for full context.',
    previewImage: IMG_OPPS,
    metrics: [
      { value: '100%', label: 'of active deals visible in one view' },
      { value: '8+', label: 'filter dimensions (stage, risk, rep, size, date...)' },
      { value: '30-50%', label: 'reduction in manual forecast prep time' },
      { value: '1-click', label: 'drill-down into any deal\'s full context' },
    ],
    howItWorks: [
      'All opportunities sync from your CRM in real-time, Salesforce, Zoho, or LeadSquared.',
      'Each deal shows its current stage, health score, days in stage, deal value, and assigned rep.',
      'Filter and sort by: risk level, close date, deal size, stage, rep, or custom tags.',
      'Click any deal to see the full account view: contacts, activity timeline, call summaries, and AI-recommended next steps.',
    ],
    benefits: [
      { title: 'Single Source of Truth', desc: 'No more spreadsheet backchannels. Every deal, every signal, one view.' },
      { title: 'Stage Conversion Analytics', desc: 'See conversion rates between stages and identify where deals are getting stuck in your funnel.' },
      { title: 'Smart Filtering', desc: 'Filter by risk tier, close date, deal size, or rep to instantly surface the deals that need attention.' },
      { title: 'Deep Drill-Down', desc: 'Click any deal to see the full picture: contacts, activity history, call summaries, and blockers.' },
    ],
    example: {
      title: 'A typical weekly pipeline review',
      steps: [
        { time: 'Before', text: 'Hours spent exporting CSVs, reconciling Salesforce with spreadsheets, chasing reps.' },
        { time: 'Step 1', text: 'Open the Opportunities view filtered to "This Quarter" + "At Risk".' },
        { time: 'Step 2', text: 'Sort by deal value to start with the biggest exposed revenue first.' },
        { time: 'Step 3', text: 'Click each deal to see call notes, contacts, and the recommended save play.' },
        { time: 'Step 4', text: 'Assign the play to the rep with one click, Forsyt logs it and follows up automatically.' },
      ],
    },
    faq: [
      { q: 'Can I keep using my CRM as the system of record?', a: 'Yes, Forsyt reads from and writes back to your CRM. Your CRM stays the source of truth.' },
      { q: 'Does it work with custom fields?', a: 'Yes. You can map any custom CRM field into Forsyt\'s filter and sort dimensions.' },
      { q: 'Is there a limit on the number of deals?', a: 'No practical limit for mid-market and enterprise pipelines. Dashboards stay performant at 10,000+ active opportunities.' },
    ],
    whoItsFor: ['CRO', 'Sales Manager', 'RevOps', 'Sales Reps'],
    sandboxLink: '/dashboard/opportunities',
    related: ['risk-radar', 'account-intelligence', 'pipeline-health-scoring'],
    quote: 'I like the concept and actions. The core thought is right.',
    quoteAuthor: 'Chaitanya Kakkar',
    quoteRole: 'VP - Business & International Expansion, SingleInterface',
  },
  'todays-focus': {
    title: "Today's Focus",
    icon: Target,
    headline: 'One card per rep. Zero guesswork. Maximum selling time.',
    subheadline: "Every morning, each rep sees exactly one screen: their highest-impact action, overdue follow-ups, and a prioritized task list, all powered by AI analysis of their pipeline.",
    previewImage: IMG_FOCUS,
    metrics: [
      { value: '2.5 hrs/day', label: 'reclaimed from tool-hopping and admin' },
      { value: '1 screen', label: 'surfaces the single most important action' },
      { value: '5x', label: 'faster decision on "what to work on next"' },
      { value: 'Real-time', label: 're-prioritization as the day unfolds' },
    ],
    howItWorks: [
      'Forsyt analyzes each rep\'s pipeline overnight and identifies the single highest-impact action for the day.',
      'The Focus Card shows: priority deal, the specific blocker, and the exact next step to take.',
      'Below the card, reps see overdue follow-ups ranked by revenue impact and a daily task list.',
      'As reps complete actions, Forsyt auto-updates the CRM and recalculates priorities in real-time.',
    ],
    benefits: [
      { title: 'Eliminate Decision Fatigue', desc: 'Reps don\'t choose what to work on. Forsyt tells them, based on data, not gut feel.' },
      { title: 'Reclaim 2.5 Hours/Day', desc: 'No more CRM data entry, tool-hopping, or figuring out priorities. More time selling.' },
      { title: 'Real-Time Re-Prioritization', desc: 'As deals progress or stall throughout the day, the focus card updates automatically.' },
      { title: 'Manager Visibility', desc: 'Managers see what each rep\'s focus is for the day and can intervene on misaligned priorities.' },
    ],
    example: {
      title: 'How a morning starts with Today\'s Focus',
      steps: [
        { time: '8:30 AM', text: 'Rep opens Forsyt. One card: "Acme renewal, champion gone quiet for 9 days."' },
        { time: '8:32 AM', text: 'Forsyt prescribes: "Send the ROI case study to the new VP Engineering, Sarah Chen."' },
        { time: '8:40 AM', text: 'Rep hits "Do it for me", Forsyt drafts the email using past winning templates.' },
        { time: '8:45 AM', text: 'Email sent. Focus card re-ranks: next priority is a stalled discovery call with Globex.' },
      ],
    },
    faq: [
      { q: 'What if a rep disagrees with the priority?', a: 'They can dismiss or re-rank any item. Forsyt learns from those choices and improves its recommendations over time.' },
      { q: 'Does it work for SDRs and AMs too?', a: 'Yes. The same Focus Card concept adapts to outbound cadence priority for SDRs and account health actions for AMs.' },
      { q: 'Can I customize what signals drive priority?', a: 'Managers can weight factors like deal size, close date proximity, and strategic account tags.' },
    ],
    whoItsFor: ['Sales Reps', 'SDR / BDR', 'Account Executives', 'Sales Manager'],
    sandboxLink: '/dashboard/todays-focus',
    related: ['action-prescriptions', 'ai-deal-chat', 'smart-notifications'],
    quote: 'This is what was needed, it will definitely help the sales team.',
    quoteAuthor: 'Sandeep',
    quoteRole: 'CRO',
  },
  'teams-overview': {
    title: 'Teams Overview',
    icon: Users,
    headline: 'Team performance, quota attainment, and rep activity, one view.',
    subheadline: 'Stop guessing who\'s on track and who needs help. Teams Overview gives managers and CROs real-time visibility into rep activity, pipeline health, and quota attainment.',
    previewImage: IMG_OPPS,
    metrics: [
      { value: 'Real-time', label: 'quota attainment per rep' },
      { value: 'Auto-logged', label: 'calls, emails, meetings, no manual entry' },
      { value: '1-click', label: 'switch between rep, team, and org view' },
      { value: '4x', label: 'faster to identify who needs coaching' },
    ],
    howItWorks: [
      'Each rep\'s quota, pipeline value, and attainment percentage are calculated in real-time from CRM data.',
      'Activity metrics, calls, emails, meetings, are tracked automatically. No manual logging required.',
      'Pipeline health is aggregated per rep: see how many healthy vs. at-risk deals each person is managing.',
      'Leaderboard view ranks reps by quota attainment, activity, or pipeline value for coaching conversations.',
    ],
    benefits: [
      { title: 'Quota Tracking', desc: 'See who\'s on pace, who\'s behind, and who\'s already at risk of missing, updated daily.' },
      { title: 'Activity vs. Results', desc: 'Correlate activity levels (calls, emails) with pipeline outcomes. Find reps who are busy but not productive.' },
      { title: 'Coaching Priorities', desc: 'Instantly see which reps need coaching and on which deals, based on data, not 1:1 self-reporting.' },
      { title: 'Team Health Score', desc: 'An aggregate health score for your entire team\'s pipeline, so you know the state of the business at a glance.' },
    ],
    example: {
      title: 'Monday morning standup, with Teams Overview',
      steps: [
        { time: '9:00 AM', text: 'Manager opens Teams Overview, sees 2 of 8 reps are trending below 70% quota attainment.' },
        { time: '9:05 AM', text: 'Clicks a rep to see their pipeline, 4 deals stalled, 1 at-risk.' },
        { time: '9:10 AM', text: 'Opens deal-level risk signals, manager knows what to coach on before the 1:1.' },
        { time: '9:30 AM', text: '1:1 becomes specific: "Let\'s multi-thread on Acme" instead of "How\'s your pipeline?"' },
      ],
    },
    faq: [
      { q: 'Can I see historical trends per rep?', a: 'Yes. Weekly, monthly, and quarterly views show attainment and activity trends over time.' },
      { q: 'Is rep activity tracked without their consent?', a: 'No. Activity tracking is enabled per user with full transparency on what\'s logged.' },
      { q: 'Does it integrate with sales engagement tools?', a: 'Yes, Outreach, Salesloft, and native email sequences feed into activity metrics.' },
    ],
    whoItsFor: ['CRO', 'VP Sales', 'Sales Manager', 'RevOps'],
    sandboxLink: '/dashboard/teams',
    related: ['pipeline-health-scoring', 'role-based-views', 'action-prescriptions'],
    quote: 'Every revenue leader knows that deals slip because of a lack of follow-through, not a lack of forecasting data.',
    quoteAuthor: 'Rajath KN',
    quoteRole: 'Associate Director, Ex-IBM / Ex-Whatfix',
  },
  'ai-deal-chat': {
    title: 'AI Deal Chat',
    icon: MessageSquare,
    headline: 'Ask anything about any deal. Get instant, data-backed answers.',
    subheadline: 'AI Deal Chat lets you query your pipeline in natural language. Ask "What\'s blocking the Acme deal?" and get an answer synthesized from calls, emails, CRM data, and activity history.',
    previewImage: IMG_FOCUS,
    metrics: [
      { value: '<3 sec', label: 'median response time with full deal context' },
      { value: '0 prep', label: 'walk into deal reviews without exports' },
      { value: '5+', label: 'data sources synthesized per answer' },
      { value: 'Verified', label: 'every fact cites a source and timestamp' },
    ],
    howItWorks: [
      'Type a question about any deal, rep, or account in natural language.',
      'Forsyt\'s AI synthesizes context from CRM fields, call transcripts, email threads, and activity logs.',
      'Responses include specific data points, timestamps, and source references so you can verify.',
      'Use it in deal reviews, 1:1s, or ad-hoc, it\'s always available and always up-to-date.',
    ],
    benefits: [
      { title: 'Instant Deal Context', desc: 'No more digging through Salesforce, Gong, and Slack. Ask a question, get the answer.' },
      { title: 'Prep-Free Deal Reviews', desc: 'Walk into any deal review and ask the AI for a summary. Save hours of manual prep.' },
      { title: 'Cross-Signal Intelligence', desc: 'The AI connects dots across tools, a negative call sentiment + stalled CRM stage = proactive risk flag.' },
      { title: 'Natural Language', desc: 'No dashboards to learn. No filters to configure. Just ask what you want to know.' },
    ],
    example: {
      title: 'A CRO prepping for the board in 5 minutes',
      steps: [
        { time: 'Prompt', text: '"Summarize the top 10 deals closing this quarter and what\'s blocking the at-risk ones."' },
        { time: 'Step 1', text: 'Forsyt ranks by value, filters to the current quarter, and classifies each by health.' },
        { time: 'Step 2', text: 'For at-risk deals, it quotes the specific signal, "Champion unresponsive since March 28".' },
        { time: 'Step 3', text: 'Every line cites its source, click to jump to the call or email that triggered it.' },
      ],
    },
    faq: [
      { q: 'What LLM powers Deal Chat?', a: 'A hybrid of frontier models for reasoning and in-house retrieval for pipeline data, configurable on customer demand.' },
      { q: 'Does my data leave our environment?', a: 'No. Prompts and responses are processed in-memory. Nothing is persisted on Forsyt servers. Enterprise deployments support your own VPC.' },
      { q: 'Can I bookmark or share answers?', a: 'Yes, any answer can be saved to a deal or shared into Slack/email for team visibility.' },
    ],
    whoItsFor: ['Sales Reps', 'Sales Manager', 'CRO', 'RevOps'],
    sandboxLink: '/dashboard',
    related: ['account-intelligence', 'action-prescriptions', 'todays-focus'],
    quote: 'It bridges the gap between data and execution.',
    quoteAuthor: 'Chaitanya Kakkar',
    quoteRole: 'VP - Business & International Expansion, SingleInterface',
  },
  'account-intelligence': {
    title: 'Account Intelligence',
    icon: Eye,
    headline: 'Deep account insights, contacts, history, risk factors, next steps.',
    subheadline: 'Every account page in Forsyt is a complete intelligence briefing: contact maps, engagement timelines, deal risk factors, and AI-recommended next actions.',
    previewImage: IMG_OPPS,
    metrics: [
      { value: '360°', label: 'view of every account in one screen' },
      { value: '12+', label: 'risk patterns detected automatically' },
      { value: 'Every', label: 'touchpoint logged across Sales + CS + Support' },
      { value: '1 page', label: 'replaces Salesforce + Gong + Slack drill-down' },
    ],
    howItWorks: [
      'Click any account to see a 360-degree view: contacts, deal history, activity timeline, and health score.',
      'Contact mapping shows everyone involved in the deal, their role, engagement level, and last touchpoint.',
      'The activity timeline aggregates calls, emails, meetings, and CRM updates in chronological order.',
      'AI surfaces risk factors (single-threaded, stalled, no exec sponsor) and recommends specific next steps.',
    ],
    benefits: [
      { title: 'Complete Account Picture', desc: 'Everything you need to know about an account on one page. No tool-hopping.' },
      { title: 'Contact Intelligence', desc: 'See who\'s engaged, who\'s gone quiet, and who you need to bring into the conversation.' },
      { title: 'Risk Factor Analysis', desc: 'AI identifies deal-level risks: single-threading, missing stakeholders, stalled stages, negative sentiment.' },
      { title: 'Prescriptive Next Steps', desc: 'Don\'t just see the problem, see exactly what to do about it, tailored to this specific account.' },
    ],
    example: {
      title: 'Inheriting an account and getting up to speed in 10 minutes',
      steps: [
        { time: 'Minute 1', text: 'Open the account, see contact map with engagement heat: 6 contacts, 2 engaged, 4 silent.' },
        { time: 'Minute 3', text: 'Scan the activity timeline, last demo was 22 days ago, no follow-up recorded.' },
        { time: 'Minute 5', text: 'AI summary highlights the primary blocker: "pricing concern raised on the last call."' },
        { time: 'Minute 10', text: 'Recommended next step: "Share Globex case study with procurement sponsor."' },
      ],
    },
    faq: [
      { q: 'Does Account Intelligence work for post-sale CS teams?', a: 'Yes. The same view surfaces health signals, renewal risk, and expansion patterns for existing customers.' },
      { q: 'Can I export an account briefing?', a: 'Yes, one-click export to PDF or Slack for account handovers and leadership reviews.' },
      { q: 'How many contacts can it map?', a: 'No hard limit. Enterprise accounts with 50+ contacts render smoothly.' },
    ],
    whoItsFor: ['Sales Reps', 'Account Executives', 'Customer Success Manager', 'Sales Manager'],
    sandboxLink: '/dashboard/opportunities',
    related: ['opportunities-pipeline', 'ai-deal-chat', 'deal-risk-signals'],
    quote: 'The real-time nudges can completely change a sales team\'s follow-through.',
    quoteAuthor: 'Aditya Ghosh',
    quoteRole: 'Lead Product Manager, Techjockey',
  },
  'action-prescriptions': {
    title: 'Action Prescriptions',
    icon: Zap,
    headline: 'Stop asking "what happened?" Start executing "what\'s next."',
    subheadline: 'Forsyt doesn\'t just show you data, it tells every rep the exact next step for every deal, based on stage, signals, and historical win patterns.',
    previewImage: IMG_FOCUS,
    metrics: [
      { value: '1 click', label: '"Do it for me" draft emails and tasks' },
      { value: 'Stage-aware', label: 'prescriptions change as deals progress' },
      { value: 'Top-rep', label: 'patterns informing every recommendation' },
      { value: 'Measurable', label: 'track prescription → outcome correlation' },
    ],
    howItWorks: [
      'For every active deal, Forsyt analyzes the current stage, recent activity, contact engagement, and call sentiment.',
      'Based on this analysis, it prescribes a specific action: "Send follow-up email to VP Engineering", "Schedule multi-thread call", "Share ROI case study".',
      'Prescriptions are ranked by impact, the highest-value actions surface first in Today\'s Focus.',
      'As reps complete actions, new prescriptions are generated. The system learns from what works and refines recommendations.',
    ],
    benefits: [
      { title: 'Eliminates Guesswork', desc: 'Reps know exactly what to do and in what order. No more "I\'ll figure it out" deal management.' },
      { title: 'Based on Win Patterns', desc: 'Prescriptions are informed by your historical win/loss data, what top reps do differently.' },
      { title: 'Drives Consistency', desc: 'Every rep follows the same high-quality playbook, not just the top performers.' },
      { title: 'Measurable Impact', desc: 'Track prescription completion rates and correlate them with deal outcomes.' },
    ],
    example: {
      title: 'A deal moving through three prescription cycles',
      steps: [
        { time: 'Week 1', text: 'Prescription: "Schedule technical validation call with security lead at Acme."' },
        { time: 'Week 2', text: 'Prescription: "Send pricing proposal with 3-year TCO breakdown."' },
        { time: 'Week 3', text: 'Risk signal: "Procurement slowing"; prescription: "Share peer reference from Globex."' },
        { time: 'Outcome', text: 'Deal closes at +12% ACV vs. average, system logs the winning pattern.' },
      ],
    },
    faq: [
      { q: 'What if a prescription isn\'t relevant?', a: 'Reps dismiss or edit it. Forsyt learns which types of plays work for which segments.' },
      { q: 'How are plays sourced?', a: 'From your org\'s winning historical data plus curated best practices from 200+ CRO conversations.' },
      { q: 'Can enablement add custom plays?', a: 'Yes, upload playbooks, methodologies (MEDDIC/BANT), or custom cadences to inform Forsyt.' },
    ],
    whoItsFor: ['Sales Reps', 'Sales Manager', 'Enablement'],
    sandboxLink: '/dashboard/todays-focus',
    related: ['todays-focus', 'ai-deal-chat', 'smart-notifications'],
    quote: 'The biggest challenge in revenue isn\'t knowing what to do, it\'s getting reps to consistently do it.',
    quoteAuthor: 'Aditya Ghosh',
    quoteRole: 'Lead Product Manager, Techjockey',
  },
  'pipeline-health-scoring': {
    title: 'Pipeline Health Scoring',
    icon: LineChart,
    headline: 'AI-scored pipeline. No more rep self-assessments.',
    subheadline: 'Every deal in your pipeline gets an objective health score based on real signals, activity frequency, contact depth, stage velocity, and sentiment, not what the rep tells you in a forecast call.',
    previewImage: IMG_RISK_RADAR,
    metrics: [
      { value: '6+', label: 'dimensions feeding every score' },
      { value: 'Daily', label: 'updates, no manual refresh needed' },
      { value: 'Objective', label: 'replaces "happy ears" forecasting' },
      { value: 'Trend', label: 'indicators show deals improving or decaying' },
    ],
    howItWorks: [
      'Forsyt pulls signals from CRM, calls, emails, and calendar to build a multi-dimensional health score for each deal.',
      'Scoring factors: days in current stage, email response rates, call frequency, number of contacts engaged, meeting cadence, and sentiment trends.',
      'Scores update daily and are visible at the deal, rep, team, and pipeline level.',
      'Trend indicators show whether a deal is improving or deteriorating over time.',
    ],
    benefits: [
      { title: 'Objective Scoring', desc: 'Eliminates the "happy ears" problem. Scores are based on data, not rep optimism.' },
      { title: 'Forecast Confidence', desc: 'CROs can trust the pipeline number because it\'s grounded in real activity, not self-reported stages.' },
      { title: 'Trend Detection', desc: 'See whether deals are trending up or down before the rep notices, or admits it.' },
      { title: 'Aggregate Health', desc: 'Roll up deal scores to rep, team, and pipeline level for a true state-of-the-business view.' },
    ],
    example: {
      title: 'Scoring vs. reality: a deal that looked safe',
      steps: [
        { time: 'Rep says', text: '"Stage 4, on track, close next Friday."' },
        { time: 'Forsyt sees', text: 'No calls in 10 days. Last email bounced. Budget holder unengaged. Score: 42/100.' },
        { time: 'Trend', text: 'Decayed from 78 → 42 over the last 3 weeks, silent deterioration.' },
        { time: 'Action', text: 'Risk Radar raises a flag 14 days before the expected close, saving the deal.' },
      ],
    },
    faq: [
      { q: 'Can we weight the scoring dimensions?', a: 'Yes. RevOps can tune weights by segment, deal size, or product line.' },
      { q: 'Does scoring replace sales stages?', a: 'No, it complements stages. Score is orthogonal to stage and gives a "reality check" on each stage claim.' },
      { q: 'Can I see the factors that produced a score?', a: 'Every score is explainable, click to see the contributing signals and their weights.' },
    ],
    whoItsFor: ['CRO', 'RevOps', 'Sales Manager'],
    sandboxLink: '/dashboard/risk-radar',
    related: ['risk-radar', 'deal-risk-signals', 'teams-overview'],
    quote: 'We have enough dashboards telling us what went wrong. Forsyt steps in to fix behaviour before deals are lost.',
    quoteAuthor: 'Chaitanya Kakkar',
    quoteRole: 'VP - Business & International Expansion, SingleInterface',
  },
  'deal-risk-signals': {
    title: 'Deal Risk Signals',
    icon: Shield,
    headline: 'Know exactly why a deal is at risk, and what to do about it.',
    subheadline: 'Forsyt identifies specific risk patterns in your deals: single-threading, champion gone quiet, stalled negotiations, missing decision-makers, and prescribes the fix.',
    previewImage: IMG_RISK_RADAR,
    metrics: [
      { value: '12+', label: 'discrete risk signal patterns' },
      { value: '3-tier', label: 'severity ratings (High/Medium/Low)' },
      { value: 'Instant', label: 'Slack + email + in-app alerts' },
      { value: 'Specific', label: 'mitigation action for every signal' },
    ],
    howItWorks: [
      'Forsyt monitors 12+ risk signal types across every active deal in your pipeline.',
      'Signals include: single-threaded deal, champion disengaged, stage stalled 14+ days, no executive sponsor, competitor mentioned, budget concern raised.',
      'Each signal comes with a severity rating (High, Medium, Low) and a recommended mitigation action.',
      'Signals are surfaced in Risk Radar, deal detail pages, and daily rep notifications.',
    ],
    benefits: [
      { title: 'Specific, Not Vague', desc: 'Not just "deal at risk", but exactly WHY and exactly what to do about it.' },
      { title: 'Multi-Signal Analysis', desc: 'A deal might look fine on stage alone, but Forsyt catches the silent killer: champion gone quiet + no exec sponsor.' },
      { title: 'Proactive Alerts', desc: 'Get notified the moment a new risk signal appears, don\'t wait for the weekly pipeline review.' },
      { title: 'Pattern Recognition', desc: 'Forsyt learns which risk combinations most often lead to lost deals in YOUR org.' },
    ],
    example: {
      title: 'Catching a deal 3 weeks before the expected slip',
      steps: [
        { time: 'Signal 1', text: 'Champion (VP Ops) unresponsive for 9 days, flagged Medium.' },
        { time: 'Signal 2', text: 'Only 1 contact at the account engaged, single-threaded, flagged High.' },
        { time: 'Signal 3', text: 'Competitor mentioned twice on the last call, flagged Medium.' },
        { time: 'Combined', text: 'Compound score: Critical. Recommended: multi-thread + competitive displacement play.' },
      ],
    },
    faq: [
      { q: 'Can we add org-specific risk signals?', a: 'Yes, custom rules (e.g., "Legal team not looped in by Stage 4") can be defined by RevOps.' },
      { q: 'Do alerts fatigue reps?', a: 'No, signals are dedup\'d and only re-fired when state changes. Most reps see ≤3 alerts per day.' },
      { q: 'Are signals explainable?', a: 'Every signal shows the evidence: the specific call, email, or CRM event that triggered it.' },
    ],
    whoItsFor: ['Sales Manager', 'CRO', 'Sales Reps'],
    sandboxLink: '/dashboard/risk-radar',
    related: ['risk-radar', 'pipeline-health-scoring', 'account-intelligence'],
    quote: 'Building a platform whose sole purpose is to drive critical actions is a massive step forward.',
    quoteAuthor: 'Rajath KN',
    quoteRole: 'Associate Director, Ex-IBM / Ex-Whatfix',
  },
  'crm-auto-sync': {
    title: 'CRM Auto-Sync',
    icon: GitBranch,
    headline: 'Your CRM updates itself. Reps sell.',
    subheadline: 'Forsyt automatically writes to CRM fields based on call transcripts, email activity, and meeting outcomes, eliminating the #1 time-waster for sales reps.',
    previewImage: IMG_OPPS,
    metrics: [
      { value: '2.5 hrs/day', label: 'saved per rep on manual data entry' },
      { value: '3 CRMs', label: 'native support, Salesforce, Zoho, LeadSquared' },
      { value: '100%', label: 'of updates auditable with source attribution' },
      { value: 'No code', label: 'field mapping, point-and-click setup' },
    ],
    howItWorks: [
      'After every call, Forsyt extracts key information: next steps discussed, objections raised, stakeholders mentioned, and timeline updates.',
      'This data is mapped to your CRM fields (deal stage, next step, close date, notes) and written automatically.',
      'Email activity (sent, received, opens) syncs continuously to the deal record.',
      'Reps can review and edit auto-filled fields before they\'re finalized, maintaining control while saving hours.',
    ],
    benefits: [
      { title: '2.5 Hours/Day Saved', desc: 'The average rep spends 2.5 hours daily on data entry. Forsyt gives that time back.' },
      { title: 'Clean CRM Data', desc: 'RevOps gets accurate, timely data without chasing reps. Fields are updated after every interaction.' },
      { title: 'Works With Your CRM', desc: 'Salesforce, Zoho, LeadSquared, Forsyt maps to your existing field structure. No migration required.' },
      { title: 'Audit Trail', desc: 'Every auto-update is logged with the source (call, email, meeting) so you can trace any change.' },
    ],
    example: {
      title: 'Zero-touch CRM update after a discovery call',
      steps: [
        { time: 'Call ends', text: 'Rep hangs up, Forsyt processes the transcript within 60 seconds.' },
        { time: 'Fields updated', text: 'Stage → Qualified, Next Step → "Send RFP", Close Date → Apr 15, Notes auto-summarized.' },
        { time: 'Review', text: 'Rep gets a one-click review card, edit or approve in 10 seconds.' },
        { time: 'Logged', text: 'Each field change shows its source: "Updated from call on Apr 2 at 2:34pm."' },
      ],
    },
    faq: [
      { q: 'What if the transcript gets something wrong?', a: 'Reps always review before fields finalize. Corrections feed back into the model so accuracy improves.' },
      { q: 'Does it support custom CRM fields?', a: 'Yes, any custom field can be mapped in setup with a simple drag-and-drop UI.' },
      { q: 'Is call data stored?', a: 'Transcripts are processed in-memory and not retained on Forsyt servers. Only the extracted field updates are persisted.' },
    ],
    whoItsFor: ['Sales Reps', 'RevOps', 'Sales Manager'],
    sandboxLink: '/dashboard/settings',
    related: ['action-prescriptions', 'todays-focus', 'role-based-views'],
    quote: 'This is what was needed, it will definitely help the sales team.',
    quoteAuthor: 'Sandeep',
    quoteRole: 'CRO',
  },
  'role-based-views': {
    title: 'Role-Based Views',
    icon: Users,
    headline: 'Every persona sees what matters to them. Nothing more, nothing less.',
    subheadline: 'Forsyt adapts its interface based on who\'s logged in. CROs see pipeline health and team performance. Reps see today\'s focus and deal actions. RevOps sees process compliance.',
    previewImage: IMG_OPPS,
    metrics: [
      { value: '5 personas', label: 'native views (CRO / Mgr / Rep / RevOps / CS)' },
      { value: 'Zero training', label: 'every role sees exactly what they need' },
      { value: '1 platform', label: 'no separate tools for leadership and reps' },
      { value: '1-click', label: 'managers switch into any rep\'s view' },
    ],
    howItWorks: [
      'When a user logs in, Forsyt detects their role (CRO, Sales Manager, Rep, RevOps, CS) and loads the appropriate view.',
      'CRO View: Pipeline health dashboard, team performance, risk radar, forecast overview.',
      'Rep View: Today\'s Focus card, AI Deal Chat, action items, account intelligence.',
      'Manager View: Team activity, deal-level risk, coaching priorities, rep pipeline comparison.',
      'RevOps View: Data hygiene scores, process compliance, stage conversion metrics.',
    ],
    benefits: [
      { title: 'Zero Learning Curve', desc: 'Each role sees exactly what they need. No training required. No dashboard customization needed.' },
      { title: 'Reduces Noise', desc: 'Reps don\'t see CRO-level metrics. CROs don\'t see individual task lists. Signal-to-noise stays high.' },
      { title: 'One Platform, All Personas', desc: 'No need for separate tools for leadership vs. reps. Forsyt adapts to the user.' },
      { title: 'Instant Switching', desc: 'Managers can switch between their own view and any rep\'s view with one click.' },
    ],
    example: {
      title: 'Same platform, three different experiences',
      steps: [
        { time: 'CRO logs in', text: 'Sees pipeline by risk tier, team attainment trend, and an exec summary.' },
        { time: 'Manager logs in', text: 'Sees their 8 reps with coaching priority sorted by deal-level risk.' },
        { time: 'Rep logs in', text: 'Sees one Focus Card, today\'s task list, and AI Deal Chat, nothing else.' },
        { time: 'Result', text: 'Zero training rollout. Adoption hits 80%+ in the first 2 weeks.' },
      ],
    },
    faq: [
      { q: 'Can I build custom roles?', a: 'Yes, admins can define new personas and choose which modules each one sees.' },
      { q: 'Do permissions tie to roles?', a: 'Role-based access control ensures each persona only sees data they\'re entitled to.' },
      { q: 'What about hybrid roles (player-coach)?', a: 'Users can toggle between Manager and Rep views with one click.' },
    ],
    whoItsFor: ['CRO', 'Sales Manager', 'Sales Reps', 'RevOps'],
    sandboxLink: '/dashboard',
    related: ['teams-overview', 'todays-focus', 'pipeline-health-scoring'],
    quote: 'The core thought is right, show pipeline health based on stages and the actions depending on them.',
    quoteAuthor: 'Chaitanya Kakkar',
    quoteRole: 'VP - Business & International Expansion, SingleInterface',
  },
  'smart-notifications': {
    title: 'Smart Notifications',
    icon: Bell,
    headline: 'The right alert, to the right person, at the right time.',
    subheadline: 'Forsyt sends intelligent notifications when deals need attention, not when a field changes. Alerts are context-aware, role-appropriate, and always include a recommended action.',
    previewImage: IMG_FOCUS,
    metrics: [
      { value: '~3/day', label: 'average alerts per rep, signal over noise' },
      { value: '3 channels', label: 'Slack, email, in-app, meet teams where they work' },
      { value: '100%', label: 'of alerts include a recommended next step' },
      { value: 'Role-routed', label: 'rep / manager / CRO alerts are different' },
    ],
    howItWorks: [
      'Forsyt monitors all pipeline activity and triggers notifications only when something meaningful changes: a deal goes at-risk, a champion goes quiet, a close date is pushed.',
      'Notifications are routed to the right person: deal owner gets action alerts, manager gets risk alerts, CRO gets pipeline-level alerts.',
      'Each notification includes: what happened, why it matters, and what to do about it.',
      'Delivered via Slack, email, or in-app, wherever your team already works.',
    ],
    benefits: [
      { title: 'Signal, Not Noise', desc: 'No "field updated" spam. Only meaningful, action-worthy notifications that require a response.' },
      { title: 'Role-Appropriate', desc: 'Reps get deal-level actions. Managers get team-level risks. CROs get pipeline-level shifts.' },
      { title: 'Always Actionable', desc: 'Every notification comes with a recommended next step. Not just "heads up", but "here\'s what to do."' },
      { title: 'Multi-Channel', desc: 'Meet your team where they already work, Slack, email, or the Forsyt app.' },
    ],
    example: {
      title: 'A single day of smart alerts',
      steps: [
        { time: '10:12 AM', text: 'Rep Slack alert: "Acme champion opened your deck 3 times, reply now with pricing."' },
        { time: '2:30 PM', text: 'Manager email: "3 deals on your team went At Risk overnight. Review before 1:1s."' },
        { time: '4:45 PM', text: 'CRO in-app banner: "Q2 pipeline coverage dropped below 3x, see at-risk deals."' },
        { time: 'Day-end', text: 'Rep sees a zero-inbox feeling: every alert had a clear, done-in-2-minutes action.' },
      ],
    },
    faq: [
      { q: 'Can alerts be muted?', a: 'Yes, per channel, per category, per time window. Users fully control their notification preferences.' },
      { q: 'How are Slack alerts routed?', a: 'Direct message to the user by default; channel mentions can be enabled for team-level alerts.' },
      { q: 'Does Forsyt send digest summaries?', a: 'Yes, optional daily or weekly digests roll up all alerts by severity.' },
    ],
    whoItsFor: ['Sales Reps', 'Sales Manager', 'CRO', 'Customer Success Manager'],
    sandboxLink: '/dashboard/todays-focus',
    related: ['risk-radar', 'action-prescriptions', 'todays-focus'],
    quote: 'The real-time nudges can completely change a sales team\'s follow-through.',
    quoteAuthor: 'Aditya Ghosh',
    quoteRole: 'Lead Product Manager, Techjockey',
  },
};

/* ═══════ SMALL PRESENTATIONAL SUBCOMPONENTS ═══════ */
function LogoChip({ logo }) {
  if (logo.src) {
    return (
      <div className="flex flex-col items-center gap-2">
        <div className="w-12 h-12 rounded-xl bg-white shadow-sm border border-gray-100 flex items-center justify-center">
          <img src={logo.src} alt={logo.name} className="w-7 h-7 object-contain" />
        </div>
        <span className="text-xs font-medium text-gray-600">{logo.name}</span>
      </div>
    );
  }
  // Salesforce inline SVG
  if (logo.name === 'Salesforce') {
    return (
      <div className="flex flex-col items-center gap-2">
        <div className="w-12 h-12 rounded-xl bg-white shadow-sm border border-gray-100 flex items-center justify-center">
          <svg width="28" height="28" viewBox="0 0 24 24">
            <path fill="#00A1E0" d="M10.006 5.415a4.195 4.195 0 013.045-1.306c1.56 0 2.954.9 3.69 2.205.63-.3 1.35-.45 2.1-.45 2.85 0 5.159 2.34 5.159 5.22s-2.31 5.22-5.176 5.22c-.345 0-.69-.044-1.02-.104a3.75 3.75 0 01-3.3 1.95c-.6 0-1.155-.15-1.65-.375A4.314 4.314 0 018.88 20.4a4.302 4.302 0 01-4.05-2.82c-.27.062-.54.076-.825.076-2.204 0-4.005-1.8-4.005-4.05 0-1.5.811-2.805 2.01-3.51-.255-.57-.39-1.2-.39-1.846 0-2.58 2.1-4.65 4.65-4.65 1.53 0 2.85.705 3.72 1.8"/>
          </svg>
        </div>
        <span className="text-xs font-medium text-gray-600">Salesforce</span>
      </div>
    );
  }
  // Zoho placeholder
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="w-12 h-12 rounded-xl bg-white shadow-sm border border-gray-100 flex items-center justify-center" style={{ color: logo.color || '#666' }}>
        <span className="font-bold text-base" style={{ color: logo.color }}>
          {logo.name.charAt(0)}
        </span>
      </div>
      <span className="text-xs font-medium text-gray-600">{logo.name}</span>
    </div>
  );
}

function RelatedCard({ slug }) {
  const rel = productsData[slug];
  if (!rel) return null;
  const Icon = rel.icon;
  return (
    <Link
      to={`/product/${slug}`}
      className="group block p-5 rounded-2xl border border-gray-200 bg-white hover:border-blue-300 hover:shadow-md transition-all"
      data-testid={`related-product-${slug}`}
    >
      <div className="flex items-center gap-3 mb-2">
        <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
          <Icon className="w-4 h-4 text-blue-600" />
        </div>
        <h4 className="text-sm font-bold text-gray-900 group-hover:text-blue-700">{rel.title}</h4>
      </div>
      <p className="text-xs text-gray-600 leading-relaxed line-clamp-2">{rel.headline}</p>
      <div className="mt-3 flex items-center gap-1 text-xs font-semibold text-blue-600">
        Explore <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
      </div>
    </Link>
  );
}

export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const data = productsData[slug];

  if (!data) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="pt-32 pb-20 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Product not found</h1>
          <Link to="/" className="text-blue-600 hover:underline">Back to home</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const Icon = data.icon;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO
        title={`${data.title}, Forsyt Revenue OS`}
        description={data.subheadline}
        path={`/product/${slug}`}
      />

      {/* Hero */}
      <section className="pt-20 pb-10 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 border border-blue-100 mb-5">
            <Icon className="w-3.5 h-3.5 text-blue-600" />
            <span className="text-xs font-bold text-blue-700 uppercase tracking-widest">{data.title}</span>
          </div>
          {STAGE_MAP[slug] && (
            <div className="mb-5">
              <Link to={`/#${STAGE_MAP[slug].toLowerCase()}`} data-testid="product-stage-badge" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-900 text-white text-xs font-semibold hover:bg-gray-800 transition-colors">
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-white/20 text-[9px] font-bold">{['Crawl','Walk','Run'].indexOf(STAGE_MAP[slug]) + 1}</span>
                Rollout stage: {STAGE_MAP[slug]}
                <span className="text-white/60 font-normal hidden sm:inline">· {STAGE_BLURB[STAGE_MAP[slug]]}</span>
              </Link>
            </div>
          )}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-5 leading-tight tracking-tight" data-testid="product-headline">
            {data.headline}
          </h1>
          <p className="text-base md:text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto" data-testid="product-subheadline">
            {data.subheadline}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => navigate(data.sandboxLink)}
              className="inline-flex items-center px-7 py-3 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg transition-all"
              data-testid="product-cta-primary"
            >
              Try {data.title} in Sandbox
              <ArrowRight className="ml-2 h-4 w-4" />
            </button>
            <a
              href="https://calendar.app.google/nswB9pt6ALLZ8xoy7"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-7 py-3 text-sm font-semibold border border-gray-300 hover:border-blue-600 bg-white rounded-full transition-all"
              data-testid="product-cta-secondary"
            >
              Book a Demo
            </a>
          </div>
        </div>
      </section>

      {/* Product preview image */}
      {data.previewImage && (
        <section className="pb-12 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-5xl mx-auto px-6">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-gray-200 bg-white">
              <img
                src={data.previewImage}
                alt={`${data.title} dashboard preview in Forsyt Revenue OS`}
                className="w-full h-auto block"
                loading="lazy"
              />
              <div className="absolute top-3 left-3 flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Metrics strip */}
      {data.metrics && (
        <section className="py-10 bg-gray-50 border-y border-gray-100">
          <div className="max-w-5xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {data.metrics.map((m, i) => (
                <div key={i} className="text-center" data-testid={`metric-${i}`}>
                  <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-1">
                    {m.value}
                  </div>
                  <p className="text-xs md:text-sm text-gray-600 leading-snug">{m.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* How It Works */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-xs font-bold text-blue-700 uppercase tracking-widest mb-3">
              <Sparkles className="w-3 h-3" /> How it works
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
              From signal to action, under the hood
            </h2>
          </div>
          <div className="space-y-3">
            {data.howItWorks.map((step, i) => (
              <div key={i} className="flex items-start gap-4 p-5 bg-gray-50 rounded-xl border border-gray-100 hover:border-blue-200 transition-colors" data-testid={`how-step-${i}`}>
                <span className="text-sm font-bold text-white bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full w-7 h-7 flex items-center justify-center shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <p className="text-sm md:text-[15px] text-gray-700 leading-relaxed">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-xs font-bold text-emerald-700 uppercase tracking-widest mb-3">
              <CheckCircle className="w-3 h-3" /> Why teams choose it
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
              Built for measurable impact
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {data.benefits.map((benefit, i) => (
              <div key={i} className="bg-white p-6 rounded-2xl border border-gray-200 hover:border-blue-200 hover:shadow-md transition-all" data-testid={`benefit-${i}`}>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-blue-600 shrink-0" />
                  <h3 className="text-base md:text-lg font-bold text-gray-900">{benefit.title}</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Day-in-life workflow */}
      {data.example && (
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 text-xs font-bold text-amber-700 uppercase tracking-widest mb-3">
                <Clock className="w-3 h-3" /> In practice
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
                {data.example.title}
              </h2>
            </div>
            <div className="relative pl-6 md:pl-8 space-y-6 border-l-2 border-dashed border-blue-200">
              {data.example.steps.map((step, i) => (
                <div key={i} className="relative" data-testid={`example-step-${i}`}>
                  <div className="absolute -left-[35px] md:-left-[41px] top-1 w-6 h-6 rounded-full bg-blue-600 ring-4 ring-white flex items-center justify-center">
                    <Activity className="w-3 h-3 text-white" />
                  </div>
                  <div className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-5">
                    <span className="text-xs font-bold text-blue-600 uppercase tracking-widest whitespace-nowrap md:min-w-[80px]">{step.time}</span>
                    <p className="text-sm md:text-[15px] text-gray-700 leading-relaxed">{step.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Works with your stack */}
      <section className="py-14 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-6">Works with your existing stack</p>
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
            {STACK_LOGOS.map((logo) => (
              <LogoChip key={logo.name} logo={logo} />
            ))}
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">Built for</p>
          <div className="flex flex-wrap items-center justify-center gap-2.5">
            {data.whoItsFor.map((role, i) => (
              <span key={i} className="px-4 py-1.5 bg-blue-50 text-blue-700 text-xs md:text-sm font-semibold rounded-full border border-blue-100">
                {role}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {data.faq && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-50 text-xs font-bold text-purple-700 uppercase tracking-widest mb-3">
                <AlertTriangle className="w-3 h-3" /> FAQ
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
                Common questions about {data.title}
              </h2>
            </div>
            <div className="space-y-3">
              {data.faq.map((item, i) => (
                <details key={i} className="group bg-white rounded-xl border border-gray-200 open:border-blue-200 open:shadow-sm transition-all" data-testid={`faq-${i}`}>
                  <summary className="flex items-center justify-between cursor-pointer p-5 text-sm md:text-base font-bold text-gray-900">
                    <span>{item.q}</span>
                    <span className="ml-4 text-blue-600 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                  </summary>
                  <p className="px-5 pb-5 text-sm text-gray-600 leading-relaxed">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Quote */}
      <section className="py-14 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <blockquote className="text-lg md:text-xl italic text-gray-700 leading-relaxed mb-5" data-testid="product-quote">
            "{data.quote}"
          </blockquote>
          <div className="flex items-center justify-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center font-bold text-sm">
              {data.quoteAuthor.trim().charAt(0).toUpperCase()}
            </div>
            <div className="text-left">
              <p className="text-sm font-bold text-gray-900">{data.quoteAuthor}</p>
              <p className="text-xs text-gray-500">{data.quoteRole}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related products */}
      {data.related && data.related.length > 0 && (
        <section className="py-16 bg-gray-50 border-t border-gray-100">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-8">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">You might also like</p>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 tracking-tight">Related capabilities</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {data.related.map((s) => (
                <RelatedCard key={s} slug={s} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-3 tracking-tight">
            See {data.title} with your own pipeline
          </h2>
          <p className="text-base md:text-lg text-blue-100 mb-7 max-w-2xl mx-auto">
            Explore it in the interactive sandbox with sample data, or book a 30-minute demo and see it running against your real deals.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => navigate(data.sandboxLink)}
              className="inline-flex items-center px-7 py-3 text-sm font-semibold bg-white text-blue-700 hover:bg-blue-50 rounded-full shadow-lg transition-all"
            >
              Try in Sandbox
              <ArrowRight className="ml-2 h-4 w-4" />
            </button>
            <a
              href="https://calendar.app.google/nswB9pt6ALLZ8xoy7"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-7 py-3 text-sm font-semibold border border-white/40 hover:border-white hover:bg-white/10 rounded-full transition-all"
            >
              Book a Demo
            </a>
          </div>
        </div>
      </section>

      <HowForsytRollsIn />

      <Footer />
    </div>
  );
}
